/**
 * What this person owes.
 *
 * The home screen for everyone. A nurse opens the product to find out whether
 * she is behind on anything, and the answer should be the first thing on the
 * page rather than something she has to go looking for in a list of six
 * hundred policies. An approver opens it to find what is waiting on their
 * signature; an owner, to see what is coming due. All of it is here, in the
 * order it is overdue.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { hasAtLeast } from '../../types/roles';
import {
  closedBecause,
  isClosed,
  isOutstanding,
  isSettled,
  sortByUrgency,
  useMyAcknowledgements,
  type Acknowledgement,
} from './useAcknowledgements';
import { useAddons } from '../org/useAddons';
import { GettingStarted } from './GettingStarted';
import { useLoadableApprovals, stepForInstance } from '../approvals/approvalsData';
import { useMyFormAssignments } from '../forms/useForms';
import { useMyAssignments as useMyCompetencyAssignments } from '../competencies/useCompetencies';
import { useMyReviews } from '../policies/useMyReviews';
import { formatDate } from '../policies/policyTable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './MyTasks.module.css';

/** Whole days until a deadline, negative once it has passed. */
function daysUntil(iso: string | null, now = new Date()): number | null {
  if (!iso) return null;
  const due = new Date(iso + 'T00:00:00');
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/**
 * The deadline, in the words somebody would use.
 *
 * "Due in 3 days" is actionable; "due 19 Sep 2026" needs the reader to work
 * out what day it is. Both are shown — the phrase to act on, the date to
 * check against a calendar.
 */
export function deadlinePhrase(dueDate: string | null): { text: string; urgent: boolean } {
  const days = daysUntil(dueDate);
  if (days === null) return { text: 'No deadline', urgent: false };
  if (days < 0) return { text: `${Math.abs(days)} days overdue`, urgent: true };
  if (days === 0) return { text: 'Due today', urgent: true };
  if (days === 1) return { text: 'Due tomorrow', urgent: true };
  return { text: `Due in ${days} days`, urgent: days <= 7 };
}

/**
 * One requirement, in one of three shapes.
 *
 * A BUTTON IS OFFERED ONLY WHERE THE SERVER WOULD ACCEPT ONE. The row used to
 * have two shapes, done and not-done, and everything that was not recognizably
 * done fell into not-done with a live "Review and acknowledge" on it. A
 * requirement the server had closed therefore invited an attestation it would
 * then refuse, and the reader had no way to clear it. Finding R4.
 */
function AckRow({ ack }: { ack: Acknowledgement }) {
  const deadline = deadlinePhrase(ack.dueDate);

  return (
    <li className={own.row}>
      <div className={own.main}>
        <Link to={`/policy/${ack.policyId}`} className={own.link}>
          {ack.policyTitle}
        </Link>
        <div className={own.meta}>
          <span className="tnum">{ack.policyNumber}</span>
          {ack.versionNumber != null && (
            <>
              <span aria-hidden="true"> · </span>
              <span>Version {ack.versionNumber}</span>
            </>
          )}
          {ack.dueDate && isOutstanding(ack) && (
            <>
              <span aria-hidden="true"> · </span>
              <span className="tnum">{formatDate(ack.dueDate)}</span>
            </>
          )}
        </div>
        {/* Why it closed, where the server recorded a reason. */}
        {ack.status === 'cancelled' && (
          <div className={own.meta}>{closedBecause(ack.cancelledReason)}</div>
        )}
        {ack.status === 'unknown' && (
          <div className={own.meta}>
            StandingDocs cannot tell what state this is in. Ask an administrator to look at it.
          </div>
        )}
      </div>

      {isSettled(ack) ? (
        <div className={own.doneCell}>
          <span className={`${styles.tag} ${styles.ok}`}>
            {ack.status === 'waived' ? 'Waived' : 'Acknowledged'}
          </span>
          {ack.acknowledgedAt && (
            <div className={own.meta}>
              <span className="tnum">{formatDate(ack.acknowledgedAt)}</span>
            </div>
          )}
        </div>
      ) : isClosed(ack) ? (
        <div className={own.doneCell}>
          <span className={`${styles.tag} ${styles.neutral}`}>
            {ack.status === 'cancelled' ? 'No longer required' : 'Unavailable'}
          </span>
        </div>
      ) : (
        <div className={own.actionCell}>
          <span className={deadline.urgent ? own.urgent : own.calm}>{deadline.text}</span>
          <Link
            to={`/acknowledge/${ack.id}/${ack.policyId}`}
            className={`${styles.btn} ${styles.btnPrimary}`}
          >
            Review and acknowledge
          </Link>
        </div>
      )}
    </li>
  );
}

/** A row that is not an acknowledgement: something to do, with a link. */
function TaskRow({ title, meta, dueDate, to, action }: { title: string; meta: string; dueDate: string | null; to: string; action: string }) {
  const deadline = deadlinePhrase(dueDate);
  return (
    <li className={own.row}>
      <div className={own.main}>
        <Link to={to} className={own.link}>{title}</Link>
        <div className={own.meta}>{meta}</div>
      </div>
      <div className={own.actionCell}>
        {dueDate && <span className={deadline.urgent ? own.urgent : own.calm}>{deadline.text}</span>}
        <Link to={to} className={`${styles.btn} ${styles.btnPrimary}`}>{action}</Link>
      </div>
    </li>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "a, b and c", for a sentence rather than a list. */
function listOf(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function MyTasksPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];
  const owner = hasAtLeast(roles, 'author');

  const acks = useMyAcknowledgements(orgId, uid);
  const addons = useAddons(orgId);
  const approvals = useLoadableApprovals(Boolean(session));
  const forms = useMyFormAssignments(orgId, uid, addons.data?.formsAttestation === true);
  const competencies = useMyCompetencyAssignments(orgId, uid, addons.data?.competencyEvidence === true);
  const reviews = useMyReviews(owner);

  if (acks.isLoading) return <Spinner label="Loading your tasks" />;

  if (acks.error) {
    return (
      <PageState title="Could not load your tasks" tone="alert">
        {acks.error instanceof Error ? acks.error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  /**
   * A QUERY THAT FAILED IS NOT A PERSON WITH NOTHING TO DO. Finding J.
   *
   * Five separate queries build this page and four of them ended in `?? []`,
   * so a network fault, a server error or a missing index turned into zero
   * tasks and the page said "You are up to date" — to somebody who might have
   * an overdue policy waiting. Each category now says whether it actually
   * answered. What loaded is still shown; what did not is named; and the
   * all-clear is withheld unless every category that was asked came back.
   */
  const categories = [
    { name: 'documents waiting for your approval', q: approvals },
    { name: 'policies to acknowledge', q: acks },
    ...(addons.data?.formsAttestation === true ? [{ name: 'forms', q: forms }] : []),
    ...(addons.data?.competencyEvidence === true ? [{ name: 'competencies', q: competencies }] : []),
    ...(owner ? [{ name: 'policies you own that are due for review', q: reviews }] : []),
  ];
  const unchecked = categories.filter((c) => c.q.isError);
  const stillChecking = categories.some((c) => c.q.isLoading);
  const retryFailed = () => { for (const c of unchecked) void c.q.refetch(); };

  const all = sortByUrgency(acks.data ?? []);
  const outstanding = all.filter(isOutstanding);
  const completed = all.filter(isSettled);
  // Closed without anybody reading anything. Kept on the page, because a nurse
  // who was chased about a policy last week should be able to see what became
  // of it, but counted as nothing and offering nothing.
  const closed = all.filter(isClosed);
  const overdueCount = outstanding.filter((a) => a.status === 'overdue').length;

  const waitingApprovals = approvals.data?.instances ?? [];
  const openForms = (forms.data ?? []).filter((f) => f.status === 'pending' || f.status === 'overdue');
  const openCompetencies = (competencies.data ?? []).filter((c) => c.status === 'pending' || c.status === 'overdue');
  const dueReviews = reviews.data ?? [];

  const parts: string[] = [];
  if (waitingApprovals.length) parts.push(`${plural(waitingApprovals.length, 'document', 'documents')} waiting for your approval`);
  if (outstanding.length) {
    parts.push(
      `${plural(outstanding.length, 'policy needs', 'policies need')} your acknowledgement` +
      (overdueCount > 0 ? `, ${overdueCount} of them overdue` : ''),
    );
  }
  if (openForms.length) parts.push(`${plural(openForms.length, 'form', 'forms')} to complete`);
  if (openCompetencies.length) parts.push(`${plural(openCompetencies.length, 'competency', 'competencies')} needing evidence`);
  if (dueReviews.length) parts.push(`${plural(dueReviews.length, 'policy you own is', 'policies you own are')} due for review`);
  // Only when everything that was asked answered, and all of it was empty.
  const nothing = parts.length === 0 && unchecked.length === 0 && !stillChecking;

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Your tasks</h1>
        <p className={styles.subtitle}>
          {nothing
            ? 'Nothing is waiting for you.'
            : parts.length > 0
              ? `${parts.join('; ')}.`
              : unchecked.length > 0
                ? 'Some of your tasks could not be checked.'
                : 'Checking your tasks\u2026'}
        </p>
      </header>

      <div className={styles.body}>
        {/* A new organization's administrator lands here with nothing waiting
            and nothing explained. The getting-started list is the explanation,
            and it ticks itself as the record fills in. */}
        {orgId && hasAtLeast(roles, 'admin') && <GettingStarted orgId={orgId} roles={roles} />}

        {unchecked.length > 0 && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="alert">
            <p>
              We couldn&rsquo;t check your {listOf(unchecked.map((c) => c.name))}. Anything waiting for you
              there is not shown below, so this page is not the whole picture.
            </p>
            <button type="button" className={styles.btn} onClick={retryFailed}>Try again</button>
          </div>
        )}

        {nothing && (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            You are up to date. Anything new will appear here, and you will be
            emailed when it does.
          </div>
        )}

        {waitingApprovals.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Waiting for your approval</h2>
            <ul className={styles.list}>
              {waitingApprovals.map((inst, i) => {
                const step = stepForInstance(approvals.data, inst, i);
                return (
                  <TaskRow
                    key={inst.instanceId}
                    title={inst.artifactTitle ? `${inst.artifactNumber ? `${inst.artifactNumber} ` : ''}${inst.artifactTitle}` : 'A document'}
                    meta={[
                      inst.artifactVersionNumber != null ? `Version ${inst.artifactVersionNumber}` : null,
                      inst.submittedByName ? `submitted by ${inst.submittedByName}` : null,
                      step?.name ?? null,
                    ].filter(Boolean).join(' · ')}
                    dueDate={null}
                    to="/my-approvals"
                    action="Decide"
                  />
                );
              })}
            </ul>
          </section>
        )}

        {outstanding.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Waiting for you</h2>
            <ul className={styles.list}>
              {outstanding.map((a) => (
                <AckRow key={a.id} ack={a} />
              ))}
            </ul>
          </section>
        )}

        {openForms.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Forms to complete</h2>
            <ul className={styles.list}>
              {openForms.map((f) => (
                <TaskRow key={f.id} title={f.formTitle} meta={`Form · version ${f.formVersionNumber}`} dueDate={f.dueDate}
                  to={`/forms/assignments/${f.id}`} action="Complete" />
              ))}
            </ul>
          </section>
        )}

        {openCompetencies.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Competencies needing evidence</h2>
            <ul className={styles.list}>
              {openCompetencies.map((c) => (
                <TaskRow key={c.id} title={c.competencyTitle}
                  meta={c.evidenceId ? 'Evidence submitted, awaiting approval' : `Competency · version ${c.competencyVersionNumber}`}
                  dueDate={c.dueDate}
                  to={c.evidenceId ? `/competencies/${c.competencyId}` : `/competencies/assignments/${c.id}`}
                  action={c.evidenceId ? 'View' : 'Submit evidence'} />
              ))}
            </ul>
          </section>
        )}

        {dueReviews.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Policies you own, due for review</h2>
            <ul className={styles.list}>
              {dueReviews.map((r) => (
                <TaskRow key={r.id} title={r.policyNumber ? `${r.policyNumber} ${r.title}` : r.title}
                  meta={r.reviewStatus === 'overdue' ? 'Past its review date' : 'Review coming due'}
                  dueDate={r.nextReviewAt} to={`/policy/${r.id}`} action="Review" />
              ))}
            </ul>
          </section>
        )}

        {closed.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              No longer required
              <span className={own.count}> · {closed.length}</span>
            </h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              Closed without an acknowledgement, because the policy or your membership changed.
              There is nothing for you to do.
            </p>
            <ul className={styles.list}>
              {closed.map((a) => (
                <AckRow key={a.id} ack={a} />
              ))}
            </ul>
          </section>
        )}

        {completed.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Completed
              <span className={own.count}> · {completed.length}</span>
            </h2>
            {/* Kept on the page rather than hidden behind a tab: this list is
                the record somebody points at when asked to prove they read a
                policy, which is most of why the product is bought. */}
            <ul className={styles.list}>
              {completed.map((a) => (
                <AckRow key={a.id} ack={a} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
